import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Brand } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [plan, setPlan] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (pollCount.current >= MAX_POLLS) { setState("timeout"); return; }
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPlan(data.plan);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-slate-50/90 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center"><Brand /></div>
      </header>

      <div className="max-w-md mx-auto px-6 pt-24 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={40} className="mx-auto text-brandblue animate-spin" />
            <h1 className="font-heading text-2xl font-extrabold text-midnight mt-6">Confirming your payment…</h1>
            <p className="text-ink-secondary mt-2">This only takes a moment. Please don't close this tab.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <span className="inline-grid place-items-center w-16 h-16 rounded-full bg-[#E7F6EF] text-status-success mx-auto animate-pop-in"><CheckCircle2 size={34} /></span>
            <h1 className="font-heading text-3xl font-extrabold text-midnight mt-6">You're on {plan || "PaySignal"}! 🎉</h1>
            <p className="text-ink-secondary mt-2">Your sandbox payment went through. Everything on your plan is now unlocked.</p>
            <Link to="/dashboard"><Button className="mt-8" data-testid="payment-go-dashboard">Go to dashboard <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        )}
        {state === "timeout" && (
          <div data-testid="payment-timeout">
            <Loader2 size={40} className="mx-auto text-ink-muted" />
            <h1 className="font-heading text-2xl font-extrabold text-midnight mt-6">Still processing</h1>
            <p className="text-ink-secondary mt-2">Your payment is taking longer than usual to confirm. It may complete shortly — check your dashboard in a minute.</p>
            <Link to="/dashboard"><Button variant="outline" className="mt-8" data-testid="payment-timeout-dashboard">Go to dashboard</Button></Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <XCircle size={40} className="mx-auto text-status-error" />
            <h1 className="font-heading text-2xl font-extrabold text-midnight mt-6">We couldn't confirm this payment</h1>
            <p className="text-ink-secondary mt-2">No charge may have gone through. You can try again from the pricing page.</p>
            <Link to="/pricing"><Button variant="outline" className="mt-8" data-testid="payment-error-pricing">Back to pricing</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
